/**
 * workflowDisplayName — the AM-facing name for a workflow id when no
 * registered title is in hand.
 *
 * The registered title (the catalog's `name`, the brief's
 * `recommended_workflow_title`) always wins; callers pass it when they hold
 * it. This is the fallback for the paths that only know the id: a drawer
 * opened from a notification, a ready-to-start card read in compact form, a
 * run record whose catalog read failed. Those paths used to title-case the
 * dispatch key, so `carrier_mortgagee_update_v2` read "Carrier Mortgagee
 * Update V2" on one surface while the catalog said something else (story
 * 28633: the workflow is "Mortgagee Change" everywhere).
 *
 * The table holds only ids whose registered title differs from the
 * title-cased id. It mirrors the workflows registry (`title=` on the
 * orchestration) and must change with it. An UNPUBLISHED workflow belongs
 * here too: the pane's catalog no longer lists it, so an old run of it would
 * otherwise read as its dispatch key.
 *
 * Shared — no Office.js, no Remix import.
 */

export const KNOWN_WORKFLOW_TITLES: Readonly<Record<string, string>> = {
  carrier_mortgagee_update_v2: "Mortgagee Change",
  // Unpublished 2026-10-08 (story 28633); its runs still open in the drawer.
  mortgagee_update_v1_2: "Carrier Mortgagee Update",
};

/** "policy_check_v3a" -> "Policy Check V3a". The last resort, never preferred. */
function titleCase(id: string): string {
  return id.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The name to show for `workflowId`. A non-blank `registeredTitle` wins, then
 * the known-title table, then the title-cased id. Accepts a catalog ref
 * (`"<namespace>/<id>"`) as well as a bare id. Null only for a blank id.
 */
export function workflowDisplayName(
  workflowId: string | null | undefined,
  registeredTitle?: string | null,
): string | null {
  const title = registeredTitle?.trim();
  if (title) return title;
  const raw = (workflowId ?? "").trim();
  if (!raw) return null;
  const id = raw.includes("/") ? (raw.split("/")[1] ?? raw) : raw;
  return KNOWN_WORKFLOW_TITLES[id] ?? titleCase(id);
}

export default workflowDisplayName;
